import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Eye } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PersonCell from '../../components/ui/PersonCell';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Tabs from '../../components/ui/Tabs';
import ApprovalFlowTimeline from '../../components/ui/ApprovalFlowTimeline';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';
import api from '../../services/api';
import { getUserApprovalFlows, getApprovalFlowDetail } from '../../services/approvalFlowService';
import { formatDate } from '../../utils/Utils';
import ExpensePolicyNotice from './components/ExpensePolicyNotice';
import { describeSavedExpensePolicy, formatPolicyAmount } from './config/expensePolicyHelpers';

const Container = styled.div``;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 14px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 20px;
  background: ${({ $status }) =>
    $status === 'approved' ? '#2e7d3215' :
    $status === 'rejected' ? '#d32f2f15' :
    $status === 'canceled' ? '#9e9e9e15' :
    '#ed6c0215'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#2e7d32' :
    $status === 'rejected' ? '#d32f2f' :
    $status === 'canceled' ? '#9e9e9e' :
    '#ed6c02'};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const InfoItem = styled.div``;

const InfoLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #A0A8B4;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SectionTitle = styled.h4`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #E8ECF0;
`;

const DescriptionText = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
  white-space: pre-wrap;
`;

const RejectReasonBox = styled.div`
  background: #d32f2f08;
  border-left: 3px solid #d32f2f;
  padding: 12px 16px;
  border-radius: 0 4px 4px 0;
  margin-top: 8px;
`;

const ReceiptCard = styled.div`
  border: 1.5px solid #E0E4E8;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: border-color 0.15s, box-shadow 0.15s;
  ${({ $clickable }) => $clickable && `
    &:hover {
      border-color: #0291EF;
      box-shadow: 0 2px 8px rgba(2, 145, 239, 0.1);
    }
  `}
`;

const ReceiptInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReceiptName = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ReceiptMeta = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #6b7280;
`;

const PreviewLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary.main};
  text-decoration: none;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const PaymentBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) => $status === 'paid' ? '#2e7d3215' : '#ed6c0215'};
  color: ${({ $status }) => $status === 'paid' ? '#2e7d32' : '#ed6c02'};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $type }) => $type === 'advance' ? '#0291ef15' : '#02d3bf15'};
  color: ${({ $type }) => $type === 'advance' ? '#0291ef' : '#02d3bf'};
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #E8ECF0;
`;

// #72 — bu ikisi ÖNCEDEN `styled(Button)` + `!important` ile Button.js'in
// varsayılan görünümünü EZİYORDU (ApproveButton düz mavi zorluyordu — artık
// gereksiz, Button.js'in "primary" varyantı zaten gradyan; RejectButton
// hardcoded #d32f2f ile Button.js'in YENİ `variant="danger"`sının BİREBİR
// aynısını elle tekrar ediyordu). İkisi de kaldırıldı — doğrudan `<Button>`/
// `<Button variant="danger">` kullanılıyor (bkz. t6-report.md).

const RejectDialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const RejectDialogBox = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const RejectDialogTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

const NoReceipts = styled.div`
  text-align: center;
  color: #A0A8B4;
  padding: 20px 0;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
`;

export default function ExpenseDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { user } = useAuth();

  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  // { fileUrl, title } | null — açık olduğunda DocumentViewerModal'ı besler. `fileUrl` KAYITLI
  // depolama adresidir (artık özel sınıf — bkz. StoragePurposes.expenseReceipt), imzalı adres
  // DEĞİL; çözümü DocumentViewerModal kendisi `/v1/storage/view` üzerinden yapar (beş özlük-dosyası
  // yüzeyiyle aynı desen, bkz. EmployeePersonnelFileTab.js).
  const [viewing, setViewing] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('detail');
  const [flowSteps, setFlowSteps] = useState([]);

  // Limit aşımı özeti — hem üstteki uyarı kutusu hem "Ödenecek Tutar" satırı
  // bunu okur; kırpma uygulanmamış eski kayıtlarda ödenecek tutar iddia edilmez.
  const savedPolicy = useMemo(() => describeSavedExpensePolicy(expense), [expense]);

  useEffect(() => {
    setLoading(true);
    api.get(`/v1/expenses/${id}`)
      .then(async (res) => {
        const expenseData = res.data?.data;
        setExpense(expenseData);

        if (expenseData?.userId) {
          try {
            // Talep başladığı akışla biter (spec 2026-11-01 §4.4): sabitlenmiş masrafta zincir O akıştan; işaretsiz
            // eski masrafta sahibin bugünkü akışından.
            let flowId = null;
            if (expenseData.approvalFlowPinned) {
              flowId = expenseData.approvalFlowId || null;
            } else {
              const flowsRes = await getUserApprovalFlows(expenseData.userId);
              const flows = flowsRes.data?.data || [];
              flowId = flows.find((f) => f.flowType === 'expense')?.approvalFlowId || null;
            }
            if (flowId) {
              const flowDetail = await getApprovalFlowDetail(flowId);
              const steps = flowDetail.data?.data?.steps || [];
              setFlowSteps(steps.slice().sort((a, b) => a.stepOrder - b.stepOrder));
            }
          } catch { /* interceptor */ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const canApproveOrReject = expense?.status === 'waiting'
    && expense?.userId !== user?.id
    && expense?.isCurrentUserApprover === true;

  const canCancel = expense?.canBeCanceled === true && expense?.userId === user?.id;

  const handleCancel = useCallback(async () => {
    setProcessing(true);
    try {
      await api.put(`/v1/expenses/${id}/cancel`);
      setExpense((prev) => prev ? { ...prev, status: 'canceled', canBeCanceled: false } : prev);
      setCancelConfirmOpen(false);
    } catch {
      // handled by interceptor
    } finally {
      setProcessing(false);
    }
  }, [id]);

  const handleApprove = useCallback(async () => {
    setProcessing(true);
    try {
      await api.put(`/v1/expenses/${id}/approve`);
      setExpense((prev) => prev ? { ...prev, status: 'approved' } : prev);
    } catch {
      // handled by interceptor
    } finally {
      setProcessing(false);
    }
  }, [id]);

  const handleReject = useCallback(async () => {
    if (!rejectReason.trim()) return;
    setProcessing(true);
    try {
      await api.put(`/v1/expenses/${id}/reject`, { rejectionReason: rejectReason.trim() });
      setExpense((prev) => prev ? { ...prev, status: 'rejected', rejectionReason: rejectReason } : prev);
      setRejectDialogOpen(false);
    } catch {
      // handled by interceptor
    } finally {
      setProcessing(false);
    }
  }, [id, rejectReason]);

  const statusLabels = {
    waiting: t('expense.waiting'),
    approved: t('expense.approved'),
    rejected: t('expense.rejected'),
    canceled: t('expense.canceled'),
  };

  const paymentLabels = {
    paid: t('expense.paid'),
    unpaid: t('expense.unpaid'),
  };

  const typeLabels = {
    expense: t('expense.expense'),
    advance: t('expense.advance'),
  };

  if (loading) return null;
  if (!expense) return null;

  return (
    <Container>
      <Breadcrumbs items={[
        { label: t('expense.myExpenses'), path: '/my-expenses' },
        { label: t('expense.detail') },
      ]} />

      <Card>
        <StatusBadge $status={expense.status}>
          {statusLabels[expense.status] || expense.status}
        </StatusBadge>

        {savedPolicy && (
          <ExpensePolicyNotice policy={savedPolicy} variant="saved" style={{ marginTop: 12 }} />
        )}

        <Tabs
          tabs={[
            { key: 'detail', label: t('expense.detailTab') },
            { key: 'flow', label: t('expense.approvalFlow') },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

        {activeTab === 'flow' ? (
          <ApprovalFlowTimeline steps={flowSteps} source={expense} />
        ) : (
        <>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>{t('expense.employee')}</InfoLabel>
            <InfoValue>
              <PersonCell name={expense.userFullName} profilePicture={expense.userProfilePicture} />
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('expense.type')}</InfoLabel>
            <InfoValue>
              <TypeBadge $type={expense.type}>{typeLabels[expense.type] || expense.type}</TypeBadge>
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('expense.category')}</InfoLabel>
            <InfoValue>{expense.categoryName}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('expense.project')}</InfoLabel>
            <InfoValue data-testid="expense-project">
              {expense.projectName
                ? `${expense.projectName}${expense.projectCode ? ` (${expense.projectCode})` : ''}`
                : t('expense.noProject')}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('expense.totalAmount')}</InfoLabel>
            <InfoValue>{formatPolicyAmount(expense.totalAmount, expense.currency)}</InfoValue>
          </InfoItem>
          {savedPolicy?.showsPayable && (
            <InfoItem>
              <InfoLabel>{t('expense.policy.payableLabel')}</InfoLabel>
              <InfoValue style={{ color: '#b71c1c', fontWeight: 700 }}>
                {formatPolicyAmount(savedPolicy.payableAmount, expense.currency)}
              </InfoValue>
            </InfoItem>
          )}
          <InfoItem>
            <InfoLabel>{t('expense.paymentStatus')}</InfoLabel>
            <InfoValue>
              <PaymentBadge $status={expense.paymentStatus}>
                {paymentLabels[expense.paymentStatus] || expense.paymentStatus}
              </PaymentBadge>
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('expense.createDate')}</InfoLabel>
            <InfoValue>{formatDate(expense.createDate, i18n.language)}</InfoValue>
          </InfoItem>
        </InfoGrid>

        {expense.description && (
          <>
            <SectionTitle>{t('expense.description')}</SectionTitle>
            <DescriptionText>{expense.description}</DescriptionText>
          </>
        )}

        {expense.rejectionReason && (
          <>
            <SectionTitle>{t('expense.rejectReason')}</SectionTitle>
            <RejectReasonBox>
              <DescriptionText>{expense.rejectionReason}</DescriptionText>
            </RejectReasonBox>
          </>
        )}

        {expense.type === 'expense' && (
          <>
            <SectionTitle>{t('expense.receipts')}</SectionTitle>
            {expense.receipts && expense.receipts.length > 0 ? (
              expense.receipts.map((receipt, idx) => (
                <ReceiptCard
                  key={idx}
                  data-testid="receipt-thumb"
                  $clickable={!!receipt.mediaUrl}
                  onClick={() => receipt.mediaUrl && setViewing({ fileUrl: receipt.mediaUrl, title: t('expense.preview') })}
                >
                  <ReceiptInfo>
                    <ReceiptMeta>
                      {receipt.amount} {expense.currency}
                      {receipt.date && ` - ${formatDate(receipt.date, i18n.language)}`}
                      {receipt.taxRate > 0 && ` - ${t('expense.receiptTaxRate')}: %${receipt.taxRate}`}
                    </ReceiptMeta>
                  </ReceiptInfo>
                  {receipt.mediaUrl && (
                    <Eye size={16} color="#0291EF" />
                  )}
                </ReceiptCard>
              ))
            ) : (
              <NoReceipts>{t('expense.noReceipts')}</NoReceipts>
            )}
          </>
        )}

        {canApproveOrReject && (
          <ActionBar>
            <Button variant="danger" onClick={() => setRejectDialogOpen(true)} disabled={processing}>
              {t('expense.reject')}
            </Button>
            <Button onClick={handleApprove} disabled={processing}>
              {processing ? '...' : t('expense.approve')}
            </Button>
          </ActionBar>
        )}

        {canCancel && (
          <ActionBar>
            <Button variant="outline" onClick={() => setCancelConfirmOpen(true)} disabled={processing}
              style={{ borderColor: '#d32f2f', color: '#d32f2f' }}>
              {t('expense.cancel')}
            </Button>
          </ActionBar>
        )}
        </>
        )}
      </Card>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('expense.cancelTitle')}
        message={t('expense.cancelMessage')}
        onConfirm={handleCancel}
        onCancel={() => setCancelConfirmOpen(false)}
        confirmText={t('expense.cancel')}
        loading={processing}
      />

      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.title}
        onClose={() => setViewing(null)}
      />

      {rejectDialogOpen && (
        <RejectDialogOverlay onClick={() => setRejectDialogOpen(false)}>
          <RejectDialogBox onClick={(e) => e.stopPropagation()}>
            <RejectDialogTitle>{t('expense.rejectTitle')}</RejectDialogTitle>
            <TextInput
              label={t('expense.rejectReason')}
              type="textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              rows={3}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)} disabled={processing}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={handleReject} disabled={processing || !rejectReason.trim()}>
                {processing ? '...' : t('expense.reject')}
              </Button>
            </div>
          </RejectDialogBox>
        </RejectDialogOverlay>
      )}
    </Container>
  );
}
